import React, { useState } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  CardBody,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Modal
} from "reactstrap";
import classnames from "classnames";
import constants from "../../../constants";
import helpers from "utils/helpers";
import EvidenceUploadCard from "./EvidenceUploadCard";
import userServices from "services/userServices";
import { useNotification } from "providers/NotificationProvider";
import { useTranslation } from "context/TranslationProvider";
import FlaggedModal from "../flagged/FlaggedModal";
import ViewUploadedID from "modals/ViewUploadedID";

const getAuthorName = (u) => {
  if (!u) return "";
  if (typeof u === "string") {
    const trimmed = u.trim();
    if (!trimmed || /^[0-9a-fA-F]{24}$/.test(trimmed)) return "";
    return trimmed;
  }
  if (typeof u !== "object") return "";
  const { firstName, lastName, name, email } = u;
  return [firstName, lastName].filter(Boolean).join(" ") || name || email || "";
};

const resolveTicketAuthor = (ticketData, primaryUser) => {
  return (
    getAuthorName(primaryUser) ||
    getAuthorName(ticketData?.updatedBy) ||
    getAuthorName(ticketData?.assignedTo) ||
    getAuthorName(ticketData?.user) ||
    getAuthorName(ticketData?.userId)
  );
};

const SupportRequestDetails = ({ ticketData, onClose }) => {
  const [activeTab, setActiveTab] = useState("details");
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [moderatorFiles, setModeratorFiles] = useState(ticketData.moderatorMediaUploads || []);
  const [resetUploadKey, setResetUploadKey] = useState(0);
  const { notify } = useNotification();

  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const toggleFlagModal = () => setIsFlagModalOpen(prev => !prev);

  const [idUrl, setIdUrl] = useState(null);
  const [viewUploadedIDOpen, setViewUploadedIDOpen] = useState(false);
  const closeUploadedID = () => setViewUploadedIDOpen(false);
  const zoomImage = (url) => {
    setIdUrl(url);
    setViewUploadedIDOpen(true);
  };

  if (!ticketData) return null;

  const statusKey = ticketData.status || "unknown";
  const statusColor = helpers.getStatusClass(statusKey, "ticket").replace("status-", "");

  const getImageUrl = (image) => {
    const folder = ticketData.source === "Dispute" ? "disputes" : "support-requests";
    return `${constants.s3Bucket}/${folder}/${image}`;
  };

  const formatDate = (date) => new Date(date).toLocaleDateString();

  const formatOrderType = (type) =>
    type ? type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "N/A";

  const handleEvidenceUpload = async (files) => {
    if (!files?.length) return;

    setUploading(true);
    const formData = new FormData();
    files.forEach((file) => formData.append("moderatorMediaUploads", file));

    try {
      const response = await userServices.updateSupportRequest(ticketData._id, formData);

      if (response?.success) {
        const updatedFiles = response.updatedRequest?.moderatorMediaUploads || response?.data?.updatedRequest?.moderatorMediaUploads || [];
        setModeratorFiles([...updatedFiles]);
        setResetUploadKey((prev) => prev + 1);
        notify("success", t(response?.messageKey || "page.support-tickets.toast.evidenceUploaded", response?.message || t("page.support-tickets.toast.evidenceUploaded", "Evidence uploaded successfully")));
      } else {
        notify("error", t(response?.messageKey || response?.error || "page.support-tickets.toast.uploadFailed", response?.message || t("page.support-tickets.toast.uploadFailed", "Failed to upload evidence")));
      }
    } catch (err) {
      console.error(err);
      notify("error", t("page.support-tickets.toast.uploadFailed", "Failed to upload evidence"));
    } finally {
      setUploading(false);
    }
  };

  const renderFiles = (files = []) => {
    if (!files.length) return <div className="text-sm text-muted">{t("common.empty", "Empty")}</div>;

    return (
      <div className="mt-2 d-flex flex-wrap gap-3">
        {files.map((file, idx) => {
          const url = getImageUrl(file);
          const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(file);

          if (!isImage) {
            return (
              <a
                key={idx}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm d-flex align-items-center"
              >
                📄 {file}
              </a>
            );
          }

          return (
            <img
              key={idx}
              src={url}
              alt={file}
              className="rounded shadow mr-2 cursor-pointer order-image mt-2"
              onClick={() => zoomImage(url)}
            />
          );
        })}
      </div>
    );
  };

  return (
    <>
      <ModalHeader className="border" toggle={onClose}>
        {t("page.support-tickets.requestDetails", "Support Request Details")}
      </ModalHeader>

      <ModalBody className="px-4 py-3">
        <Nav className="nav-pills nav-pills-primary mb-3">
          {["details", "evidence"].map((tab) => (
            <NavItem key={tab}>
              <NavLink
                className={classnames("nav-link cursor-pointer", { active: activeTab === tab })}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "details"
                  ? t("page.messages.support.title", "Support Ticket Details")
                  : t("page.support-tickets.evidence", "Evidence")}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* DETAILS TAB */}
          <TabPane tabId="details">
            {/* Created At */}
            <Row className="mb-3">
              <Col md="6">
                <small className="text-muted">{t("table.headers.createdAt", "Created At")}</small>
                <div>{formatDate(ticketData.createdAt)}</div>
              </Col>
              <Col md="6" className="text-md-right">
                <Badge className="badge-lg" color={statusColor}>
                  {t(`page.support-tickets.status.${statusKey}`)}
                </Badge>
              </Col>
            </Row>

            {/* Title + User */}
            <Row className="mb-3">
              <Col md="6">
                <small className="text-muted">{t("table.headers.title", "Title")}</small>
                <h4 className="mt-1">{ticketData.title}</h4>
              </Col>

              <Col md="6" className="text-md-right">
                <small className="text-muted">{t("page.messages.support.createdBy", "Created By")}</small>
                <div>
                  {ticketData.user?.firstName} {ticketData.user?.lastName}
                  <br />
                  <small className="text-muted">{ticketData.user?.email}</small>
                </div>
              </Col>
            </Row>

            {/* Description */}
            <Row className="mb-4">
              <Col>
                <small className="text-muted">{t("page.messages.support.description", "Description")}</small>
                <div className="border rounded p-3">{ticketData.description}</div>
              </Col>
            </Row>

            {ticketData.status !== "resolved" && ticketData.reopenReason && (
              <Row className="mb-4">
                <Col>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">{t("page.support-tickets.reopenReason", "Reopen Reason")}</small>
                    {(() => {
                      const author = resolveTicketAuthor(ticketData, ticketData?.reopenedBy);
                      return author ? <small className="text-muted font-weight-bold">{t("page.support-tickets.by", "by")} {author}</small> : null;
                    })()}
                  </div>
                  <div className="border rounded p-3 text-dark">{ticketData.reopenReason}</div>
                </Col>
              </Row>
            )}

            {ticketData.status === "resolved" && ticketData.resolutionNotes && (
              <Row className="mb-4">
                <Col>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">{t("page.support-tickets.resolutionNotes", "Resolution Notes")}</small>
                    {(() => {
                      const author = resolveTicketAuthor(ticketData, ticketData?.resolvedBy);
                      return author ? <small className="text-muted font-weight-bold">{t("page.support-tickets.by", "by")} {author}</small> : null;
                    })()}
                  </div>
                  <div className="border rounded p-3 text-dark">{ticketData.resolutionNotes}</div>
                </Col>
              </Row>
            )}

            {/* Order Info */}
            {(ticketData.orderDetails || ticketData.orderid) && (
              <Row className="mb-4">
                <Col md="6">
                  <small className="text-muted">{t("page.support-tickets.journey", "Order Journey")}</small>
                  <div>
                    {ticketData.orderDetails
                      ? `${ticketData.orderDetails.fromPlace?.name} → ${ticketData.orderDetails.toPlace?.name}`
                      : ticketData.orderid?.orderid || ticketData.orderid || "N/A"}
                  </div>
                </Col>

                <Col md="6">
                  <small className="text-muted">{t("table.headers.orderType", "Order Type")}</small>
                  <div>{formatOrderType(ticketData.orderType)}</div>
                </Col>

                <Col md="6" className="mt-2">
                  <small className="text-muted">{t("page.support-tickets.contactForm.form.categoryLabel", "Category")}</small>
                  <div className="fw-medium">{helpers.getCategoryLabel(ticketData.category)}</div>
                </Col>
              </Row>
            )}

            {ticketData.updatedBy && (
              <Row className="mb-4">
                <Col md="6">
                  <small className="text-muted">{t("page.support-tickets.updatedBy", "Updated By")}</small>
                  <div>
                    {ticketData.updatedBy.firstName} {ticketData.updatedBy.lastName}
                    <br />
                    <small className="text-muted">{ticketData.updatedBy.email}</small>
                  </div>
                </Col>
              </Row>
            )}
          </TabPane>

          <TabPane tabId="evidence">
            <Row>
              <Col lg="6" className="mb-2">
                <Card className="shadow-sm h-100">
                  <CardBody>
                    <div className="text-sm font-weight-bold mb-3">{t("page.support-tickets.userEvidence", "Uploaded by User")}</div>
                    {renderFiles(ticketData.mediaUploads)}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="6" className="mb-2">
                <Card className="shadow-sm h-100">
                  <CardBody>
                    <div className="text-sm font-weight-bold mb-3">{t("page.support-tickets.moderatorEvidence", "Uploaded by Admin/Moderator")}</div>
                    {renderFiles(moderatorFiles)}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Card>
              <EvidenceUploadCard
                key={resetUploadKey}
                title={t("page.support-tickets.uploadEvidence", "Upload Evidence")}
                loading={uploading}
                onUpload={handleEvidenceUpload}
              />
            </Card>
          </TabPane>
        </TabContent>

        <div className="text-right mt-4">
          <Button color="secondary" onClick={onClose} className="mr-0">
            {t("button.close", "Close")}
          </Button>
          {statusKey === "pending" && (
            <Button color="warning" onClick={toggleFlagModal} className="ml-2">
              {t("page.flagged.flagThisContent", "Flag this content")}
            </Button>
          )}
        </div>
        <Modal
          isOpen={isFlagModalOpen}
          toggle={toggleFlagModal}
          size="lg"
          backdrop="static"
          className="modal-dialog-centered"
        >
          <ModalBody className="p-0">
            <FlaggedModal
              isOpen={isFlagModalOpen}
              toggle={toggleFlagModal}
              contentCategory="ticket"
              contentId={ticketData._id}
              user={ticketData.user || {}}
            />
          </ModalBody>
        </Modal>
        <ViewUploadedID
          idUrl={idUrl}
          isOpen={viewUploadedIDOpen}
          toggle={closeUploadedID}
        />
      </ModalBody>
    </>
  );
};

export default SupportRequestDetails;
